import {
  InputHTMLAttributes,
  Ref,
  forwardRef,
  useEffect,
  useState,
} from 'react';

import { IconCheckRound } from '@/components/apps/ui/icon';

import theme from '@/styles/theme';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  disabled?: boolean;
  label?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/** 원본에서 CheckboxContainer 가 `${LabelText} { margin-right: 16px }` 로 항상 붙여주던 값 포함 */
const LABEL_TEXT_BASE = 'mr-[16px] leading-[140%] tracking-[-0.3px]';

const CONTAINER_BASE =
  'relative inline-flex w-fit cursor-pointer items-center text-[15px] font-normal';

const CHECKBOX_BASE =
  'block h-[20px] w-[20px] cursor-pointer rounded-[4px] border bg-center bg-no-repeat';

/*
 * 원본 선언 순서: 기본 -> &:checked -> &:disabled -> &:disabled &:checked.
 * :checked 와 :disabled 는 명시도가 같아 뒤에 선언된 :disabled 가 이겼다.
 * 유틸리티는 전부 !important 라 순서에 기댈 수 없으므로 disabled prop 으로 승자를 계산한다.
 * (disabled 는 props 에서 구조분해로 빠져 나오므로 이 prop 이 유일한 소스다)
 */
function checkboxStateClass(disabled?: boolean) {
  if (disabled) {
    // :disabled 가 :checked 의 배경/테두리를 덮고, 체크 상태에서는 blend 만 추가된다
    return 'border-apps-coreProductBorder bg-apps-coreProductDisableContainer checked:bg-blend-color-burn';
  }
  return 'border-apps-coreProductBorder bg-apps-coreGrayWhite checked:border-apps-corePrimary01 checked:bg-apps-corePrimary01';
}

const Checkbox = forwardRef<HTMLInputElement, InputProps>(
  ({ disabled, label, onChange, ...props }, ref: Ref<HTMLInputElement>) => {
    const [isChecked, setIsChecked] = useState(props.defaultChecked);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsChecked(e.target.checked);
      onChange?.(e);
    };

    useEffect(() => {
      if (props.checked !== undefined) {
        setIsChecked(props.checked);
      }
    }, [props.checked]);

    return (
      // label이 있는 경우에만 마이너스 처리
      <label className={`${CONTAINER_BASE} ${label ? 'ml-[-8px]' : ''}`}>
        <span className="relative p-[8px]">
          <input
            className={`${CHECKBOX_BASE} ${checkboxStateClass(disabled)}`}
            type="checkbox"
            ref={ref}
            disabled={disabled}
            onChange={handleChange}
            {...props}
          />
          <span className="pointer-events-none absolute top-1/2 -translate-y-1/2 select-none">
            {isChecked && disabled && (
              <IconCheckRound
                size={20}
                color={theme`colors.apps.coreGrayBlack`}
                opacity={0.4}
              />
            )}
            {isChecked && !disabled && (
              <IconCheckRound
                size={20}
                color={theme`colors.apps.coreGrayBlack`}
              />
            )}
          </span>
        </span>

        {label && (
          /* disabled 스타일: 원본에서 뒤에 선언된 disabled 색이 기본 색을 덮으므로 한쪽만 emit */
          <span
            className={`${LABEL_TEXT_BASE} ${
              disabled
                ? 'text-apps-coreProductDisableContent'
                : 'text-apps-coreGrayBlack'
            }`}
            dangerouslySetInnerHTML={{ __html: label }}
          />
        )}
      </label>
    );
  },
);

export default Checkbox;
